// 30-day stacked-bars overview band. One bar per UTC day; bar height = total
// AI discourse volume that day; segments = visible companies + Others.
//
// Folding membership ("Others") is driven by the treemap, not computed
// independently — so a tile that's visible in the treemap is also a visible
// segment here, and hover cross-links cleanly in both directions.
//
// Hover a bar OR a legend chip to highlight that company across both this
// chart and the treemap. Drag the bottom edge to resize. Toggle "normalize"
// to switch between absolute volume bars and 100% share-of-voice bars.

import { useEffect, useMemo, useRef, useState } from "react";
import { COLORS, FONT_MONO, fmtRaw } from "../styles";
import type { CountBucket } from "../../shared/types";

const DAY = 86400;
const PALETTE = [
  "#5cd9b9",
  "#8ac6ff",
  "#f4a261",
  "#e76f51",
  "#bda6ff",
  "#ffe066",
  "#f78fad",
  "#76e4f7",
  "#9be9b4",
  "#c0a3ff",
  "#ffae42",
  "#5dc1c4",
  "#ee82ee",
  "#a4e84f",
];
const OTHERS_COLOR = "#3a4a42";
const HEIGHT_KEY = "mindsharex.stackedheight";
const NORMALIZE_KEY = "mindsharex.stackednormalize";
const MIN_H = 70;
const MAX_H = 320;
const DEFAULT_H = 110;

export interface StackedSeries {
  name: string;
  buckets: CountBucket[];
}

// Overlay polylines drawn on top of the company stacked bars. Each overlay
// gets daily totals at the same y-scale as the bars (absolute mode only — see
// note on `normalize` below). Used for ad-hoc user-typed comparison queries
// so the user can see how their topic stacks against the curated set.
export interface OverlaySeries {
  // The user_query.id, used as a stable key across renders.
  id: number;
  name: string;
  buckets: CountBucket[];
}

// Bright palette distinct from the company palette — overlays are meant to
// pop against the dimmed bars.
const OVERLAY_PALETTE = [
  "#e8ff5c", // accent yellow
  "#ff7ad9",
  "#7ad9ff",
  "#ffb24a",
  "#9affb0",
];

interface Props {
  series: StackedSeries[];
  days: number;
  endSec: number;
  // Companies to lump into "Others". Sourced from the treemap so visible
  // tiles map 1:1 to visible chart segments.
  foldedNames: string[];
  // Name to highlight (from external hover, e.g., a treemap tile). Matching
  // segments stay vivid; non-matching dim. null = no highlight.
  highlightName: string | null;
  // Fired when the user hovers a segment or a legend chip in this band.
  onHover: (name: string | null) => void;
  // User-query overlays. When non-empty: bars dim, overlay polylines render,
  // and the normalize toggle is forced off (share-of-voice math doesn't mix
  // with absolute overlay counts).
  overlays?: OverlaySeries[];
}

interface DayCell {
  total: number;
  // sorted by 30d-rank order (visible companies first, then "Others")
  segments: Array<{ name: string; value: number; color: string }>;
}

function loadHeight(): number {
  try {
    const raw = localStorage.getItem(HEIGHT_KEY);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= MIN_H && n <= MAX_H) return n;
  } catch {
    /* ignore */
  }
  return DEFAULT_H;
}

function saveHeight(h: number) {
  try {
    localStorage.setItem(HEIGHT_KEY, String(h));
  } catch {
    /* ignore */
  }
}

function loadNormalize(): boolean {
  try {
    return localStorage.getItem(NORMALIZE_KEY) === "1";
  } catch {
    return false;
  }
}

function saveNormalize(v: boolean) {
  try {
    localStorage.setItem(NORMALIZE_KEY, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function StackedBars({
  series,
  days,
  endSec,
  foldedNames,
  highlightName,
  onHover,
  overlays = [],
}: Props) {
  const [bandH, setBandH] = useState<number>(loadHeight);
  const [normalizeRaw, setNormalize] = useState<boolean>(loadNormalize);
  // Normalize is incompatible with overlays — overlays carry absolute
  // counts, share-of-voice rescaling makes them incomparable. Force off
  // when any overlay is active; user can re-enable after deactivating.
  const normalize = overlays.length > 0 ? false : normalizeRaw;
  const [width, setWidth] = useState<number>(800);
  const [hover, setHover] = useState<{
    day: number;
    px: number;
    py: number;
  } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    saveHeight(bandH);
  }, [bandH]);

  useEffect(() => {
    saveNormalize(normalize);
  }, [normalize]);

  useEffect(() => {
    if (!chartRef.current) return;
    const el = chartRef.current;
    const ro = new ResizeObserver((ents) => {
      const w = ents[0].contentRect.width;
      if (w > 10) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Memo deps need a primitive for foldedNames so we don't recompute on
  // every parent render that produces a new array reference.
  const foldedKey = foldedNames.join(",");
  // Overlay key for memo deps — id+bucket-count is enough since buckets are
  // immutable per fetch; full deep compare isn't needed.
  const overlayKey = overlays.map((o) => `${o.id}:${o.buckets.length}`).join("|");

  const { dayCells, dayStarts, legend, maxTotal } = useMemo(() => {
    const dayEnd = Math.ceil(endSec / DAY) * DAY;
    const start0 = dayEnd - days * DAY;
    const dayStarts = Array.from({ length: days }, (_, i) => start0 + i * DAY);

    const dailyByName = new Map<string, number[]>();
    for (const s of series) {
      const daily = new Array<number>(days).fill(0);
      for (const b of s.buckets) {
        const idx = Math.floor((b.bucket_start - start0) / DAY);
        if (idx >= 0 && idx < days) daily[idx] += b.tweet_count;
      }
      dailyByName.set(s.name, daily);
    }

    const ranked = [...dailyByName.entries()]
      .map(([name, daily]) => ({
        name,
        total: daily.reduce((a, b) => a + b, 0),
        daily,
      }))
      .sort((a, b) => b.total - a.total)
      .filter((r) => r.total > 0);

    const foldedSet = new Set(foldedNames);
    const visible = ranked.filter((r) => !foldedSet.has(r.name));
    const folded = ranked.filter((r) => foldedSet.has(r.name));

    // Color stable by 30d rank position among visibles.
    const colorByName: Record<string, string> = {};
    visible.forEach((t, i) => {
      colorByName[t.name] = PALETTE[i % PALETTE.length];
    });

    const othersDaily = new Array<number>(days).fill(0);
    for (const r of folded) {
      for (let d = 0; d < days; d++) othersDaily[d] += r.daily[d];
    }

    const dayCells: DayCell[] = [];
    for (let d = 0; d < days; d++) {
      const segments: DayCell["segments"] = [];
      for (const t of visible) {
        if (t.daily[d] > 0)
          segments.push({
            name: t.name,
            value: t.daily[d],
            color: colorByName[t.name],
          });
      }
      if (othersDaily[d] > 0) {
        segments.push({
          name: "Others",
          value: othersDaily[d],
          color: OTHERS_COLOR,
        });
      }
      const total = segments.reduce((a, b) => a + b.value, 0);
      dayCells.push({ total, segments });
    }
    const maxTotal = Math.max(1, ...dayCells.map((c) => c.total));

    const legend: Array<{ name: string; color: string }> = visible.map(
      (t, i) => ({
        name: t.name,
        color: PALETTE[i % PALETTE.length],
      }),
    );
    if (folded.length > 0)
      legend.push({ name: "Others", color: OTHERS_COLOR });

    return { dayCells, dayStarts, legend, maxTotal };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, days, endSec, foldedKey]);

  // Per-overlay daily totals on the same day grid as the bars. Memoised so
  // hover/resize don't recompute. Color is by overlay-array index — stable
  // as long as the parent passes overlays in a consistent order.
  const overlayDailies = useMemo(() => {
    const dayEnd = Math.ceil(endSec / DAY) * DAY;
    const start0 = dayEnd - days * DAY;
    return overlays.map((o, i) => {
      const daily = new Array<number>(days).fill(0);
      for (const b of o.buckets) {
        const idx = Math.floor((b.bucket_start - start0) / DAY);
        if (idx >= 0 && idx < days) daily[idx] += b.tweet_count;
      }
      return {
        id: o.id,
        name: o.name,
        daily,
        color: OVERLAY_PALETTE[i % OVERLAY_PALETTE.length],
        total: daily.reduce((a, b) => a + b, 0),
        peak: daily.reduce((m, v) => (v > m ? v : m), 0),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayKey, days, endSec]);

  // Layout (real pixels)
  const legendH = 24;
  const grabH = 6;
  const chartH = Math.max(40, bandH - legendH - grabH);
  const pad = { l: 44, r: 10, t: 4, b: 14 };
  const innerW = Math.max(1, width - pad.l - pad.r);
  const innerH = Math.max(10, chartH - pad.t - pad.b);
  const barGap = 1;
  const barW = (innerW - barGap * (days - 1)) / days;
  const yScale = (v: number, dayTotal: number) =>
    normalize
      ? (v / Math.max(1, dayTotal)) * innerH
      : (v / maxTotal) * innerH;

  const onResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = bandH;
    const onMove = (ev: MouseEvent) => {
      const dy = ev.clientY - startY;
      setBandH(Math.max(MIN_H, Math.min(MAX_H, startH + dy)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const onChartMove = (e: React.MouseEvent) => {
    if (!chartRef.current) return;
    const r = chartRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x < pad.l || x > pad.l + innerW) {
      setHover(null);
      onHover(null);
      return;
    }
    const dayIdx = Math.min(
      days - 1,
      Math.max(0, Math.floor((x - pad.l) / (barW + barGap))),
    );
    setHover({ day: dayIdx, px: x, py: y });
    const cell = dayCells[dayIdx];
    let yCur = pad.t + innerH;
    let foundName: string | null = null;
    for (const seg of cell.segments) {
      const segH = yScale(seg.value, cell.total);
      yCur -= segH;
      if (y >= yCur && y <= yCur + segH) {
        foundName = seg.name;
        break;
      }
    }
    onHover(foundName);
  };

  const onChartLeave = () => {
    setHover(null);
    onHover(null);
  };

  const overlayActive = overlayDailies.length > 0;
  // When overlays are active, dim all company segments so overlay polylines
  // pop. Hover-highlight still wins (un-dims the matched segment).
  const dimMode = highlightName !== null || overlayActive;
  const isMatch = (name: string) => name === highlightName;

  return (
    <div
      ref={wrapRef}
      style={{
        display: "flex",
        flexDirection: "column",
        borderBottom: `1px solid ${COLORS.border}`,
        background: COLORS.bgSurface,
        userSelect: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "5px 12px",
          height: legendH,
          fontSize: 10,
          color: COLORS.textDim,
          letterSpacing: 1,
          flexWrap: "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
          boxSizing: "border-box",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ color: COLORS.textDimmer, flexShrink: 0 }}>
          VOL · 30D ·
        </span>
        {legend.map((l) => {
          const dim = dimMode && !isMatch(l.name);
          return (
            <span
              key={l.name}
              onMouseEnter={() => onHover(l.name)}
              onMouseLeave={() => onHover(null)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                cursor: "default",
                opacity: dim ? 0.35 : 1,
                transition: "opacity 80ms",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  background: l.color,
                  display: "inline-block",
                  borderRadius: 1,
                }}
              />
              <span style={{ color: COLORS.text, fontSize: 10 }}>
                {l.name}
              </span>
            </span>
          );
        })}
        {overlayDailies.length > 0 && (
          <span
            style={{
              flexShrink: 0,
              color: COLORS.textDimmer,
              paddingLeft: 4,
            }}
          >
            ·
          </span>
        )}
        {overlayDailies.map((o) => (
          <span
            key={o.id}
            onMouseEnter={() => onHover(o.name)}
            onMouseLeave={() => onHover(null)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              flexShrink: 0,
              cursor: "default",
            }}
            title={`overlay · ${o.total.toLocaleString()} mentions over ${days}d · peak ${o.peak.toLocaleString()}/day`}
          >
            <span
              style={{
                width: 14,
                height: 0,
                borderTop: `2px dashed ${o.color}`,
                display: "inline-block",
              }}
            />
            <span
              style={{
                color: o.color,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              {o.name}
            </span>
            {/* Peak/day always visible, not just on hover — when the overlay
                line is too small to see against tall company bars (e.g. niche
                topic vs OpenAI), this is the only signal of magnitude. */}
            <span
              style={{
                color: COLORS.textDim,
                fontSize: 10,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {o.peak === 0 ? "0/d" : `${fmtRaw(o.peak)}/d peak`}
            </span>
          </span>
        ))}
        <label
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            cursor: overlayActive ? "not-allowed" : "pointer",
            color: COLORS.textDim,
            fontSize: 10,
            letterSpacing: 1,
            flexShrink: 0,
            paddingLeft: 12,
            opacity: overlayActive ? 0.4 : 1,
          }}
          title={
            overlayActive
              ? "normalize disabled while overlays are active — overlays carry absolute counts"
              : "rescale every day's bar to 100% — show share of voice over time"
          }
        >
          <input
            type="checkbox"
            checked={normalize}
            disabled={overlayActive}
            onChange={(e) => setNormalize(e.target.checked)}
            style={{
              accentColor: COLORS.accent,
              cursor: overlayActive ? "not-allowed" : "pointer",
              margin: 0,
            }}
          />
          NORMALIZE
        </label>
      </div>

      <div
        ref={chartRef}
        style={{ position: "relative", height: chartH, width: "100%" }}
        onMouseMove={onChartMove}
        onMouseLeave={onChartLeave}
      >
        <svg
          width={width}
          height={chartH}
          style={{ display: "block", position: "absolute", inset: 0 }}
        >
          <line
            x1={pad.l}
            y1={pad.t + innerH}
            x2={pad.l + innerW}
            y2={pad.t + innerH}
            stroke={COLORS.border}
            strokeWidth={1}
          />
          <text
            x={pad.l - 4}
            y={pad.t + 8}
            textAnchor="end"
            fill={COLORS.textDimmer}
            fontSize={9}
            fontFamily={FONT_MONO}
          >
            {normalize ? "100%" : fmtRaw(maxTotal)}
          </text>
          <text
            x={pad.l - 4}
            y={pad.t + innerH}
            textAnchor="end"
            fill={COLORS.textDimmer}
            fontSize={9}
            fontFamily={FONT_MONO}
          >
            0
          </text>
          {dayCells.map((cell, d) => {
            const x = pad.l + d * (barW + barGap);
            let yCur = pad.t + innerH;
            const isHoverDay = hover?.day === d;
            return (
              <g key={d}>
                {cell.segments.map((seg, i) => {
                  const h = yScale(seg.value, cell.total);
                  yCur -= h;
                  const dim = dimMode && !isMatch(seg.name);
                  return (
                    <rect
                      key={i}
                      x={x}
                      y={yCur}
                      width={barW}
                      height={h}
                      fill={seg.color}
                      opacity={dim ? 0.18 : isHoverDay ? 1 : 0.85}
                      pointerEvents="none"
                    />
                  );
                })}
              </g>
            );
          })}
          {/* Overlay polylines — drawn after bars so they sit on top.
              Anchor each point to the day's bar centre on the same y-scale
              as the bars (absolute mode only — see normalize override). */}
          {overlayDailies.map((o) => {
            if (o.daily.every((v) => v === 0)) return null;
            const points = o.daily
              .map((v, d) => {
                const x = pad.l + d * (barW + barGap) + barW / 2;
                const y = pad.t + innerH - (v / maxTotal) * innerH;
                return `${x.toFixed(1)},${y.toFixed(1)}`;
              })
              .join(" ");
            const isHL = highlightName === o.name;
            return (
              <g key={`overlay-${o.id}`}>
                <polyline
                  points={points}
                  fill="none"
                  stroke={o.color}
                  strokeWidth={isHL ? 2.5 : 1.75}
                  strokeDasharray="4 3"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity={highlightName !== null && !isHL ? 0.45 : 1}
                  pointerEvents="none"
                />
                {o.daily.map((v, d) => {
                  if (v === 0) return null;
                  const cx = pad.l + d * (barW + barGap) + barW / 2;
                  // Lift dots fractionally off the baseline so a tiny value
                  // doesn't render half-clipped under the x-axis line. Keeps
                  // the comparison honest (non-zero stays distinguishable
                  // from zero) without inflating the visual position.
                  const rawCy = pad.t + innerH - (v / maxTotal) * innerH;
                  const cy = Math.min(rawCy, pad.t + innerH - 2);
                  return (
                    <circle
                      key={d}
                      cx={cx}
                      cy={cy}
                      r={2.5}
                      fill={o.color}
                      stroke={COLORS.bg}
                      strokeWidth={0.75}
                      opacity={highlightName !== null && !isHL ? 0.45 : 1}
                      pointerEvents="none"
                    />
                  );
                })}
              </g>
            );
          })}
          {dayStarts.map((ds, d) => {
            if (d % 5 !== 0 && d !== days - 1) return null;
            const x = pad.l + d * (barW + barGap) + barW / 2;
            const date = new Date(ds * 1000);
            return (
              <text
                key={d}
                x={x}
                y={chartH - 2}
                textAnchor="middle"
                fill={COLORS.textDimmer}
                fontSize={9}
                fontFamily={FONT_MONO}
              >
                {date.getUTCMonth() + 1}/{date.getUTCDate()}
              </text>
            );
          })}
        </svg>

        {hover && <Tooltip
          width={width}
          chartH={chartH}
          hover={hover}
          cell={dayCells[hover.day]}
          dayStart={dayStarts[hover.day]}
          overlays={overlayDailies.map((o) => ({
            name: o.name,
            color: o.color,
            value: o.daily[hover.day] ?? 0,
          }))}
        />}
      </div>

      <div
        onMouseDown={onResizeStart}
        title="drag to resize"
        style={{
          height: grabH,
          cursor: "row-resize",
          background: COLORS.bg,
          borderTop: `1px solid ${COLORS.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 2,
            background: COLORS.borderHi,
            borderRadius: 1,
          }}
        />
      </div>
    </div>
  );
}

function Tooltip({
  width,
  chartH,
  hover,
  cell,
  dayStart,
  overlays,
}: {
  width: number;
  chartH: number;
  hover: { day: number; px: number; py: number };
  cell: DayCell;
  dayStart: number;
  overlays: Array<{ name: string; color: string; value: number }>;
}) {
  const TT_W = 200;
  const TT_H_MAX = 180;
  const offsetX = 14;
  const wantLeft = hover.px + offsetX;
  const left =
    wantLeft + TT_W > width - 4 ? hover.px - TT_W - offsetX : wantLeft;
  const top = Math.min(Math.max(4, hover.py - 8), chartH - TT_H_MAX - 4 < 4 ? 4 : chartH - TT_H_MAX - 4);
  const date = new Date(dayStart * 1000).toISOString().slice(0, 10);
  const top6 = [...cell.segments]
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: TT_W,
        background: COLORS.bgSurface,
        border: `1px solid ${COLORS.borderHi}`,
        boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
        padding: "6px 8px",
        fontSize: 10,
        fontFamily: FONT_MONO,
        zIndex: 5,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          color: COLORS.text,
          fontWeight: 600,
          letterSpacing: 0.5,
          marginBottom: 4,
        }}
      >
        <span>{date}</span>
        <span style={{ color: COLORS.accent }}>{fmtRaw(cell.total)}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {top6.map((s) => {
          const pct = cell.total > 0 ? (s.value / cell.total) * 100 : 0;
          return (
            <div
              key={s.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: COLORS.text,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  background: s.color,
                  display: "inline-block",
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  flex: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {s.name}
              </span>
              <span
                style={{
                  color: COLORS.textDim,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtRaw(s.value)}
              </span>
              <span
                style={{
                  color: COLORS.textDimmer,
                  fontSize: 9,
                  width: 30,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%
              </span>
            </div>
          );
        })}
        {overlays.length > 0 && (
          <div
            style={{
              borderTop: `1px dashed ${COLORS.border}`,
              marginTop: 4,
              paddingTop: 4,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {overlays.map((o) => (
              <div
                key={o.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: COLORS.text,
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 0,
                    borderTop: `2px dashed ${o.color}`,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: o.color,
                    fontWeight: 600,
                  }}
                >
                  {o.name}
                </span>
                <span
                  style={{
                    color: COLORS.textDim,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {fmtRaw(o.value)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
